import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure.js";
import DocumentRef from "./DocumentRef.js";
import { ISSUE_BILLABLE_TO } from "./Issue.js";
import { COLORS } from "../constants.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";
import { cloneDeep, every, get, isEmpty } from "lodash";
import { diff } from "deep-object-diff";
import Address from "./Address.js";

// Re-export for convenience
export { ISSUE_BILLABLE_TO };

/**
 * Cap for the views that listen across resolved (or all) service issues.
 *
 * Open issues are self-limiting — they get closed. Resolved ones are not: that
 * set only ever grows, and both ServiceIssues (filter=all/closed) and
 * PreviouslyCompleted subscribed to it with no bound, so the listener's cost
 * grows forever with company history.
 *
 * Sized to be irrelevant in practice rather than to paginate — this is a
 * runaway guard, not a page size. Deliberately no `orderBy` alongside it:
 * ordering an equality-filtered query forces a composite index and drops docs
 * missing the sort field, and the pages already sort client-side. The
 * consequence is that IF the cap is ever reached the retained set is
 * arbitrary, so the pages that can search history say so rather than quietly
 * showing a truncated list.
 */
export const RESOLVED_ISSUE_LISTENER_LIMIT = 2000;

export const SERVICE_ISSUE_FIELDS = {
  // Core issue fields
  projectID: "",
  saleID: "",
  itemID: "",
  laborID: "",
  quantity: 1,

  // Service-specific fields
  serviceType: "", // warranty, chargeable, punchout, inspection, referred
  issueType: "", // broken_glass, seal_failure, hardware, poor_operation, lock_problem, other
  issueTypeDetails: {}, // Conditional fields based on issueType

  // Description & resolution
  description: "",
  resolution: "",
  isResolved: false,

  // Urgency & scheduling - see SERVICE_ISSUE_URGENCY
  // 1 = Punch list (hold for next punchout trip)
  // 2 = Normal (schedule when convenient)
  // 3 = Urgent (drop everything, respond immediately)
  urgency: 1,
  estimatedHours: 0,
  techsRequired: 1,
  manHours: 0, // Calculated: estimatedHours * techsRequired

  // Inspection tracking
  needsInspection: false, // Does this issue need a site inspection?
  inspectionScheduled: false, // Has inspection been scheduled?
  inspectionCompleted: false, // Has inspection been completed?
  inspectionCompletedDate: null,
  inspectionNotes: "",
  createWorkOrderAfterSave: false, // Flag to open work order dialog after saving

  // Parts tracking
  partsRequired: false,
  partsInStock: false, // Parts already on hand - no order needed
  partsOrdered: false,
  partsOrderedDate: null,
  partsRequiredDate: null, // Stamped when partsRequired first becomes true
  partsReceived: false,
  partsReceivedDate: null,
  partsNotes: "",
  partsOrderID: "", // Link to Order in project/sale
  // The vendor's number for the parts order, as typed at Order Parts: an
  // order number (Marvin's SNA06166) or a service request (SR26-01429).
  // Parts orders have no PO of ours, so receiving matches on this instead.
  partsOrderNumber: "",
  partsExpectedDate: null,
  partsWarehouseLocation: "", // Where parts are stored for punch list
  // Where receiving took the parts in (see RECEIVE_SERVICE_PARTS); empty
  // strings when they were marked received by hand.
  partsReceipt: {
    sessionID: "",
    truckNumber: "",
    orderPO: "",
    vendorOrderNumber: "",
    receivedBy: "",
  },
  partsMedia: [], // Photos of parts/unit captured by tech when parts are needed
  // No service visit: the customer picks the parts up or we deliver them, so
  // the issue goes to Parts to Deliver instead of Services to Schedule once
  // the parts are in. Only meaningful while partsRequired is set.
  deliveryOnly: false,
  warrantyDocuments: [], // DocumentRefs backing the warrantyDocs getter

  // Parts billing type (single enum - see PARTS_BILLING_TYPES)
  // "" | "warranty" | "warranty_full" | "chargeable" | "courtesy"
  partsBilling: "",

  // Legacy parts type booleans. These are now DERIVED from partsBilling during
  // save via applyLegacyBillingFlags() so that existing Firestore `where`
  // queries (e.g. partsChargeable == true) keep working during the transition.
  // Do not write to these directly from new code — set partsBilling instead.
  partsWarranty: false,
  warrantyIncludesLabor: false,
  partsChargeable: false,
  partsCourtesy: false,

  // Vendor warranty case. A warranty claim usually opens a case with the
  // vendor, and someone has to close it with them once the service is done.
  // The switch is set at review; Close Vendor Case on the completed issue
  // records who closed it and when (CLOSE_SERVICE_VENDOR_CASE).
  closeVendorCase: false,
  vendorCaseNumber: "",
  vendorCaseClosed: false,
  vendorCaseClosedDate: null,
  vendorCaseClosedBy: "", // User ID
  // DERIVED and stored, so Vendor Cases to Close can listen on a single
  // equality (no composite index). See deriveVendorCaseOpen.
  vendorCaseOpen: false,

  // Courtesy parts tracking
  courtesyAmount: 0, // Dollar amount of courtesy parts
  courtesyAuthorizedBy: "", // Employee ID who authorized courtesy
  courtesyManagerAlerted: false, // True if manager was alerted (amount > $500)
  courtesyManagerAlertDate: null,

  // Parts approval (for chargeable service)
  partsApprovalRequired: false, // True if chargeable
  partsApprovalStatus: "", // pending, approved, rejected
  partsApprovalDate: null,
  partsApprovalBy: "",
  partsEstimatedCost: 0,

  // Service Quote - links to Sales Lead and Sale for chargeable parts workflow
  salesLeadID: "", // Sales Lead ID - set when lead is created for this issue
  serviceSaleID: "", // Sale ID for the service quote (for chargeable parts)
  serviceSaleStatus: "", // Tracks quote status: quote_pending, quote_sent, approved, ordered, in_production, received

  // Dates for tracking
  reportedDate: null,
  completedDate: null,
  daysOpen: 0, // Calculated field

  // Tech findings (set during service visit)
  // The technician's notes from the issue card. Written by a raw update, so
  // without a declaration here sanitize() dropped them on every read and the
  // card re-opened empty -- and SAVE_SERVICE_ISSUE's whole-document set
  // deleted them from the stored issue.
  techNotes: "",
  needsQuote: false, // Needs a quote before proceeding
  requiresAnotherVisit: false, // Cannot be completed in this visit
  deferredDate: null, // Date when issue was deferred (saved but not resolved)

  // Work order linkage
  workOrderID: "",
  addedToWorkOrderDate: null,

  // Punch list
  isPunchListItem: false,
  foundAtPunchout: false,

  // Media (photos/video) - DocumentRef-shaped `{ type, info, file }` entries
  // under the "service-issue-media" type, handled by Documents/DocumentList
  // and uploaded via SAVE_DOCUMENTS, same as Issue.js's media field.
  media: [],

  // Contact
  contactID: "",

  // Service Address
  serviceAddress: {
    street: "",
    city: "",
    state: "",
    zip: "",
  },

  // Billing
  billableTo: "", // vendor, customer, internal, nonbillable

  // Flags
  isCritical: false,
  isOverdue: false, // >45 days
  locked: false,
  isReviewed: false, // True when issue has been reviewed and is ready to schedule
  reviewedDate: null, // Stamped by MARK_SERVICE_ISSUE_REVIEWED

  // On hold - a reviewed, unscheduled issue that can't be scheduled yet for a
  // reason the workflow doesn't model (customer away, site not ready...)
  onHold: false,
  onHoldReason: "",
  onHoldDate: null,
  onHoldBy: "",

  // Migration reference (if converted from QC Issue)
  originalIssueID: "",

  // Customer-reported flag
  isCustomerReported: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SERVICE_ISSUE_FIELDS);

export const SERVICE_TYPES = {
  warranty: {
    label: "Warranty",
    icon: "quality_control",
    color: "positive",
    description:
      "Within warranty period from manufacturer. Typically no cost to client.",
    defaultBillableTo: "vendor",
  },
  chargeable: {
    label: "Chargeable",
    icon: "cost",
    color: "warning",
    description:
      "Billed to owner or builder. May be for our products or others.",
    defaultBillableTo: "customer",
  },
  punchout: {
    label: "Punchout",
    icon: "punchout",
    color: "primary",
    description:
      "Courtesy inspection and adjustment of everything we sold for a project, plus installing any hardware, screens or parts ordered along the way.",
    defaultBillableTo: "nonbillable",
  },
  inspection: {
    label: "Site Inspection",
    icon: "inspection",
    color: "info",
    description: "General site walk to inspect products. No issues reported.",
    defaultBillableTo: "nonbillable",
  },
  referred: {
    label: "Referred Service",
    icon: "referred",
    color: "grey",
    description: "Service lead referred to another company.",
    defaultBillableTo: "nonbillable",
  },
};
deepFreeze(SERVICE_TYPES);

export const SERVICE_ISSUE_TYPES = {
  broken_glass: {
    label: "Broken Glass",
    icon: "glass_broken",
    color: "negative",
    fields: {
      glassWidth: { label: "Glass Width", type: "dimension", required: true },
      glassHeight: { label: "Glass Height", type: "dimension", required: true },
      daylightOpening: {
        label: "Daylight Opening",
        type: "text",
        required: false,
      },
      breakType: {
        label: "Break Type",
        type: "select",
        required: true,
        options: ["Thermal", "Impact", "Stress", "Unknown"],
      },
      hasGrids: { label: "Has Grids", type: "boolean", required: true },
      gridPattern: {
        label: "Grid Pattern",
        type: "text",
        required: false,
        showIf: "hasGrids",
      },
    },
  },
  // A failed insulated-glass seal (fogging between the panes). The fix is a
  // new glass unit, so it asks what a broken-glass reorder needs, minus the
  // break type.
  seal_failure: {
    label: "Seal Failure",
    icon: "seal_failure",
    color: "warning",
    fields: {
      glassWidth: { label: "Glass Width", type: "dimension", required: true },
      glassHeight: { label: "Glass Height", type: "dimension", required: true },
      daylightOpening: {
        label: "Daylight Opening",
        type: "text",
        required: false,
      },
      hasGrids: { label: "Has Grids", type: "boolean", required: true },
      gridPattern: {
        label: "Grid Pattern",
        type: "text",
        required: false,
        showIf: "hasGrids",
      },
    },
  },
  hardware: {
    label: "Broken/Missing Hardware",
    icon: "hardware_issue",
    color: "warning",
    fields: {
      hardwareType: {
        label: "Hardware Type",
        type: "select",
        required: true,
        options: [
          "Lock",
          "Handle",
          "Hinge",
          "Operator",
          "Screen Hardware",
          "Weatherstrip",
          "Other",
        ],
      },
      hardwareQuantity: {
        label: "Quantity",
        type: "number",
        required: true,
        default: 1,
      },
      isMissing: {
        label: "issue.missing",
        type: "boolean",
        required: true,
      },
    },
  },
  poor_operation: {
    label: "Poor Operation",
    icon: "poor_operation",
    color: "warning",
    fields: {
      operationType: {
        label: "Operation Type",
        type: "select",
        required: true,
        options: [
          "Hard to Open",
          "Hard to Close",
          "Won't Lock",
          "Won't Stay Open",
          "Drafty",
          "Water Leak",
          "Other",
        ],
      },
      // Symptoms is no longer a separate prompt: it's folded into the issue's
      // main "Symptoms/Description" box. Existing issues keep their saved
      // `issueTypeDetails.symptoms`, which the read-only view still renders.
    },
  },
  lock_problem: {
    label: "Lock Problem",
    icon: "lock_issue",
    color: "warning",
    tipsTricks:
      "Open window/door and test the hardware in the open position. If it works, it has something to do with the strike and how it engages. Make adjustments to the keeper/strikes.",
    fields: {
      lockType: {
        label: "Lock Type",
        type: "select",
        required: true,
        options: [
          "Multi-point",
          "Single Point",
          "Deadbolt",
          "Sliding Door Lock",
          "Other",
        ],
      },
      specificIssue: {
        label: "Specific Issue",
        type: "select",
        required: true,
        options: [
          "Won't Engage",
          "Won't Disengage",
          "Stiff Operation",
          "Misaligned",
          "Broken",
        ],
      },
    },
  },
  manufacturing_defect: {
    label: "Manufacturing Defect",
    icon: "manufacturing_defect",
    color: "warning",
    fields: {
      detailedDescription: {
        label: "Detailed Description",
        type: "textarea",
        required: true,
      },
    },
  },
  other: {
    label: "Other",
    icon: "issue",
    color: "grey",
    fields: {
      detailedDescription: {
        label: "Detailed Description",
        type: "textarea",
        required: true,
      },
    },
  },
};
deepFreeze(SERVICE_ISSUE_TYPES);

// Numeric keys are the stored values and must stay stable: existing issue docs
// hold them, isOverdue exempts 1, and functions/service.js keys "immediate" off
// 3. `order` is display order (most urgent first), which is NOT the key order.
export const SERVICE_ISSUE_URGENCY = {
  1: {
    order: 3,
    label: "Punch list",
    icon: "pending",
    color: "info",
    description:
      "Hold for the project's punchout trip - can be batched with other work at the same location",
    // Only offerable while the project still has a punchout coming. Once the
    // punchout is complete there is no trip left to batch onto, so the UI
    // hides this level (card: "Punch List [if not already completed]").
    requiresOpenPunchout: true,
  },
  2: {
    order: 2,
    label: "Normal",
    icon: "date",
    color: "warning",
    description: "Normal priority - schedule when convenient",
  },
  3: {
    order: 1,
    label: "Urgent",
    icon: "issue",
    color: "negative",
    description: "Must be completed right away, as soon as any parts arrive",
  },
};
deepFreeze(SERVICE_ISSUE_URGENCY);

/**
 * Urgency levels offerable for an issue, most urgent first.
 *
 * `punchoutComplete` — pass true when the project/sale has already had its
 * punchout, which drops the Punch list level.
 */
export const urgencyOptions = ({ punchoutComplete = false } = {}) =>
  Object.entries(SERVICE_ISSUE_URGENCY)
    .filter(([, config]) => !(punchoutComplete && config.requiresOpenPunchout))
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([value, config]) => ({
      value: Number(value),
      label: config.label,
      icon: config.icon,
      color: config.color,
      description: config.description,
    }));

export const SERVICE_ISSUE_STATUS = {
  reported: {
    order: 1,
    label: "Reported",
    icon: "issue",
    color: "warning",
    description: "Issue reported, awaiting review",
  },
  needs_inspection: {
    order: 2,
    label: "Needs inspection",
    icon: "inspection_needed",
    color: "info",
    description: "Site inspection required before proceeding",
  },
  awaiting_parts_order: {
    order: 3,
    label: "Awaiting parts order",
    icon: "order",
    color: "warning",
    description: "Parts needed but not yet ordered",
  },
  needs_quote: {
    order: 4,
    label: "Needs quote",
    icon: "quote",
    color: "warning",
    description: "Quote required for service parts or labor",
  },
  quote_sent: {
    order: 5,
    label: "Quote sent",
    icon: "sent",
    color: "info",
    description: "Quote sent to customer, awaiting approval",
  },
  quote_approved: {
    order: 6,
    label: "Quote approved",
    icon: "done",
    color: "positive",
    description: "Customer approved quote, ready to order parts",
  },
  parts_ordered: {
    order: 7,
    label: "Parts ordered",
    icon: "order",
    color: "info",
    description: "Parts have been ordered from vendor",
  },
  parts_in_production: {
    order: 8,
    label: "In production",
    icon: "production",
    color: "info",
    description: "Parts are being manufactured",
  },
  awaiting_parts: {
    order: 9,
    label: "Awaiting parts",
    icon: "pending",
    color: "info",
    description: "Parts ordered, waiting for receipt",
  },
  ready_for_delivery: {
    order: 10,
    label: "Ready for pickup or delivery",
    icon: "delivery",
    color: "positive",
    description:
      "Parts are in, ready for the customer to pick up or have delivered",
  },
  needs_scheduled: {
    order: 11,
    label: "Needs scheduled",
    icon: "calendar",
    color: "positive",
    description: "Ready to be scheduled for service",
  },
  on_hold: {
    order: 12,
    label: "On hold",
    icon: "on_hold",
    color: "warning",
    description: "Not ready to be scheduled",
  },
  scheduled: {
    order: 13,
    label: "Scheduled",
    icon: "date_done",
    color: "positive",
    description: "Service visit scheduled",
  },
  completed: {
    order: 14,
    label: "Completed",
    icon: "success",
    color: "positive",
    description: "Issue has been resolved",
  },
};
deepFreeze(SERVICE_ISSUE_STATUS);

/**
 * Parts billing type — a single enum replacing the legacy orthogonal booleans
 * partsWarranty / warrantyIncludesLabor / partsChargeable / partsCourtesy.
 *
 * Each entry declares whether it requires a customer quote for labor, which
 * drives the parts workflow branch of ServiceIssue.status.
 */
export const PARTS_BILLING_TYPES = {
  warranty: {
    label: "Warranty (parts only)",
    icon: "quality_control",
    color: "positive",
    description:
      "Parts covered by manufacturer warranty. A quote is still needed for labor.",
    needsQuote: true,
  },
  warranty_full: {
    label: "Warranty (parts + labor)",
    icon: "quality_control",
    color: "positive",
    description:
      "Warranty covers both parts and labor. No customer quote needed.",
    needsQuote: false,
  },
  chargeable: {
    label: "Chargeable",
    icon: "cost",
    color: "warning",
    description: "Customer is billed for parts and labor. A quote is required.",
    needsQuote: true,
  },
  courtesy: {
    label: "Courtesy",
    icon: "info",
    color: "info",
    description:
      "Company or employee covers the cost as goodwill. No quote needed.",
    needsQuote: false,
  },
};
deepFreeze(PARTS_BILLING_TYPES);

/**
 * Read-side migration: derive partsBilling from the legacy booleans if the
 * enum isn't set yet. Safe to call on any data shape — returns "" when
 * nothing is set.
 *
 * Priority when multiple legacy flags are (invalidly) true follows the
 * workflow gravity: chargeable > courtesy > warranty_full > warranty.
 */
export function derivePartsBilling(data) {
  if (data.partsBilling) return data.partsBilling;
  if (data.partsChargeable) return "chargeable";
  if (data.partsCourtesy) return "courtesy";
  if (data.partsWarranty) {
    return data.warrantyIncludesLabor ? "warranty_full" : "warranty";
  }
  return "";
}

/**
 * Write-side mirror: project partsBilling back onto the 4 legacy booleans so
 * existing Firestore `where` queries (e.g. partsChargeable == true) keep
 * returning the right documents during the transition window.
 *
 * Mutates `data` in place and returns it.
 */
export function applyLegacyBillingFlags(data) {
  const billing = data.partsBilling || "";
  data.partsWarranty = billing === "warranty" || billing === "warranty_full";
  data.warrantyIncludesLabor = billing === "warranty_full";
  data.partsChargeable = billing === "chargeable";
  data.partsCourtesy = billing === "courtesy";
  return data;
}

// The coverage types a vendor warrants, so the only ones with a vendor case.
export const VENDOR_CASE_BILLING_TYPES = Object.freeze([
  "warranty",
  "warranty_full",
]);

/**
 * The review's "close the vendor's warranty case" switch, honoured only while
 * the coverage is a warranty type: switching it to chargeable or courtesy
 * leaves no case to close, and switching back restores it.
 */
export function hasVendorCase(data) {
  return Boolean(
    data.closeVendorCase &&
      VENDOR_CASE_BILLING_TYPES.includes(derivePartsBilling(data))
  );
}

/**
 * The value of the stored `vendorCaseOpen` flag: a vendor case nobody has
 * marked closed yet. It is stamped on every whole-doc write (the constructor,
 * sanitize() and toFirestore), and an updateDoc that changes partsBilling,
 * closeVendorCase or vendorCaseClosed must write it too, or the Vendor Cases
 * to Close listener misses the issue or keeps it.
 */
export function deriveVendorCaseOpen(data) {
  return hasVendorCase(data) && !data.vendorCaseClosed;
}

export default class ServiceIssue extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SERVICE_ISSUE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.reportedDate = timestampToDate(data.reportedDate);
    data.completedDate = timestampToDate(data.completedDate);
    data.partsOrderedDate = timestampToDate(data.partsOrderedDate);
    data.partsRequiredDate = timestampToDate(data.partsRequiredDate);
    data.partsReceivedDate = timestampToDate(data.partsReceivedDate);
    data.reviewedDate = timestampToDate(data.reviewedDate);
    data.onHoldDate = timestampToDate(data.onHoldDate);
    data.vendorCaseClosedDate = timestampToDate(data.vendorCaseClosedDate);
    data.partsExpectedDate = timestampToDate(data.partsExpectedDate);
    data.partsApprovalDate = timestampToDate(data.partsApprovalDate);
    data.addedToWorkOrderDate = timestampToDate(data.addedToWorkOrderDate);
    data.deferredDate = timestampToDate(data.deferredDate);
    data.inspectionCompletedDate = timestampToDate(
      data.inspectionCompletedDate
    );
    data.courtesyManagerAlertDate = timestampToDate(
      data.courtesyManagerAlertDate
    );

    // Calculate man hours
    data.manHours = data.estimatedHours * data.techsRequired;

    // Migrate legacy parts-billing booleans onto the partsBilling enum.
    // This is non-destructive: booleans stay, and sanitize() keeps them in
    // sync with partsBilling so Firestore queries still work.
    data.partsBilling = derivePartsBilling(data);
    applyLegacyBillingFlags(data);
    data.vendorCaseOpen = deriveVendorCaseOpen(data);

    // Calculate days open
    if (data.reportedDate && !data.completedDate) {
      const now = new Date();
      data.daysOpen = Math.floor(
        (now - data.reportedDate) / (1000 * 60 * 60 * 24)
      );
      // Not overdue if it's a punch-list item waiting for the next punchout
      // trip. Punch list is flagged two ways: the urgency level and the
      // explicit flag. Both exempt.
      data.isOverdue =
        data.daysOpen > 45 && data.urgency !== 1 && !data.isPunchListItem;
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return (
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeDeletedBy(user) {
    return (
      !this.locked &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return ServiceIssue.getPath(this.id);
  }

  static getPath(id) {
    let path = `serviceIssues`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  // Every whole-doc write (SAVE_SERVICE_ISSUE's batch.set) passes through
  // here, so the stored vendorCaseOpen is restamped from the issue as written,
  // whatever the caller changed after building the instance.
  static toFirestore(issue) {
    return {
      ...issue.data,
      vendorCaseOpen: deriveVendorCaseOpen(issue.data),
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new ServiceIssue(snapshot.id, data);
  }

  // Validation

  get rules() {
    const required = (a) => Boolean(a && String(a).trim().length);
    const optional = () => true;
    const hasAddress = () => {
      const addr = this.data.serviceAddress;
      return addr && (addr.street || addr.city);
    };

    // Required: contact, service address, issue type
    // Optional: project, sale, item, service type, description
    return {
      contactID: [required],
      serviceAddress: [hasAddress],
      issueType: [(a) => a && a in SERVICE_ISSUE_TYPES],
      projectID: [optional],
      saleID: [optional],
      itemID: [optional],
      serviceType: [(a) => !a || a in SERVICE_TYPES],
      description: [optional],
      urgency: [optional],
      estimatedHours: [(a) => a >= 0],
      techsRequired: [(a) => a >= 1],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)))
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          ServiceIssue.sanitize({
            ...this.data,
            projectID: "",
            saleID: "",
            itemID: "",
          }),
          SERVICE_ISSUE_FIELDS
        )
      )
    );
  }

  isEqual(issue) {
    return (
      this.id === issue.id &&
      isEmpty(
        diff(
          ServiceIssue.sanitize(cloneDeep(this.data)),
          ServiceIssue.sanitize(cloneDeep(issue.data))
        )
      )
    );
  }

  // `media` holds DocumentRef-shaped entries, same as Issue.js's field of the
  // same name. `removeUnsaved` drops entries that haven't finished uploading,
  // for callers that compare against what is actually persisted.
  static cleanDocuments(data, removeUnsaved = false) {
    for (const field of ["media", "partsMedia"]) {
      if (data[field]) {
        data[field] = data[field].filter(
          (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
        );
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    ServiceIssue.cleanDocuments(data, removeUnsaved);
    data.manHours = data.estimatedHours * data.techsRequired;
    // Keep the legacy billing booleans in sync with partsBilling before
    // saving so Firestore `where` queries on the old fields still match.
    data.partsBilling = derivePartsBilling(data);
    applyLegacyBillingFlags(data);
    data.vendorCaseOpen = deriveVendorCaseOpen(data);
    return data;
  }

  // Formatting

  get project() {
    return this.data.projectID
      ? store.state.main.projects[this.data.projectID]
      : null;
  }

  get sale() {
    return this.data.saleID
      ? store.state.main.sales[this.data.projectID + this.data.saleID]
      : null;
  }

  get item() {
    return this.data.itemID
      ? store.state.main.saleItems[this.data.itemID]
      : null;
  }

  get labor() {
    return this.data.laborID
      ? store.state.main.saleLabors[this.data.laborID]
      : null;
  }

  get workOrder() {
    return this.data.workOrderID
      ? store.state.main.serviceWorkOrders[this.data.workOrderID]
      : null;
  }

  get partsOrder() {
    if (!this.data.partsOrderID || !this.data.projectID || !this.data.saleID) {
      return null;
    }
    return store.state.main.orders[this.data.partsOrderID] || null;
  }

  get serviceSale() {
    // Get the linked sale for chargeable parts (service quote)
    if (!this.data.serviceSaleID || !this.data.projectID) {
      return null;
    }
    return (
      store.state.main.sales[this.data.projectID + this.data.serviceSaleID] ||
      null
    );
  }

  get contact() {
    return this.data.contactID
      ? store.state.main.contacts[this.data.contactID]
      : null;
  }

  get serviceAddress() {
    return new Address(this.data.serviceAddress);
  }

  get billableTo() {
    return ISSUE_BILLABLE_TO[this.data.billableTo];
  }

  get serviceType() {
    const type = SERVICE_TYPES[this.data.serviceType];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get issueType() {
    const type = SERVICE_ISSUE_TYPES[this.data.issueType];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get urgencyType() {
    const type = SERVICE_ISSUE_URGENCY[this.data.urgency];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get partsBillingType() {
    const type = PARTS_BILLING_TYPES[this.data.partsBilling];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get status() {
    // Completed
    if (this.data.isResolved) {
      return "completed";
    }

    // Scheduled - has a work order
    if (this.data.workOrderID) {
      return "scheduled";
    }

    // Not reviewed yet - still in reported state
    if (!this.data.isReviewed) {
      return "reported";
    }

    // Held out of Services to Schedule until someone releases it
    if (this.data.onHold) {
      return "on_hold";
    }

    // Needs inspection and not yet completed
    if (this.data.needsInspection && !this.data.inspectionCompleted) {
      return "needs_inspection";
    }

    // Parts required - check parts workflow (skip if parts are in stock).
    // Parts that have arrived were ordered, whether or not anyone marked the
    // order: receiving takes in parts nobody recorded ordering, and they must
    // not leave the issue waiting on an order that already came.
    if (this.data.partsRequired && !this.data.partsInStock) {
      // Quote requirement is now a property of the billing type (see
      // PARTS_BILLING_TYPES). An unset partsBilling = no quote needed.
      // Warranty (parts only) quotes the labor, and a delivery-only issue
      // has none, so it goes straight to ordering. Chargeable still quotes
      // the parts themselves.
      const billingType = PARTS_BILLING_TYPES[this.data.partsBilling];
      const needsQuoteForLabor = billingType
        ? billingType.needsQuote &&
          !(this.isDeliveryOnly && this.data.partsBilling === "warranty")
        : false;

      if (needsQuoteForLabor) {
        // Use serviceSaleStatus which is synced from the quote's status.type
        // by the touchQuote Cloud Function trigger
        const quoteStatus = this.data.serviceSaleStatus;

        if (quoteStatus) {
          // Quote sent, awaiting response
          if (quoteStatus === "sent" || quoteStatus === "undelivered") {
            return "quote_sent";
          }

          // Quote approved - check order/production status
          if (quoteStatus.startsWith("approved") && !this.data.partsReceived) {
            return this.data.partsOrdered ? "parts_ordered" : "quote_approved";
          }

          // Quote declined - needs new quote
          if (
            quoteStatus === "declined" ||
            quoteStatus === "declined-changes"
          ) {
            return "needs_quote";
          }

          // Quote exists but not sent yet - still needs quote
          if (quoteStatus === "unsent") {
            return "needs_quote";
          }
        } else if (!this.data.serviceSaleID) {
          // No service sale linked yet - needs quote
          return "needs_quote";
        } else {
          // Sale exists but no status yet - needs quote
          return "needs_quote";
        }
      }

      // No quote needed (warranty with labor, or courtesy) - go straight to
      // parts workflow: not yet ordered, then ordered but not received
      if (!this.data.partsReceived) {
        return this.data.partsOrdered
          ? "awaiting_parts"
          : "awaiting_parts_order";
      }
    }

    // Parts are in and there's no visit to schedule
    if (this.isDeliveryOnly) {
      return "ready_for_delivery";
    }

    // Ready to schedule
    return "needs_scheduled";
  }

  // Delivery only means the parts ARE the service, so the flag is ignored
  // once parts are no longer required.
  get isDeliveryOnly() {
    return Boolean(this.data.deliveryOnly && this.data.partsRequired);
  }

  // The review asked for the vendor's warranty case to be closed, and the
  // coverage is still a warranty type.
  get hasVendorCase() {
    return hasVendorCase(this.data);
  }

  // A vendor case still to close. Read from the fields, not the stored
  // vendorCaseOpen, so an instance edited in place answers for its edits.
  get isVendorCaseOpen() {
    return deriveVendorCaseOpen(this.data);
  }

  get vendorCaseClosedByUser() {
    return this.data.vendorCaseClosedBy
      ? store.state.main.users[this.data.vendorCaseClosedBy] || null
      : null;
  }

  // The PO the warehouse tracks the job by (project ID + sale letter, the
  // default PO on every order), not the project's name or street.
  get po() {
    return (this.data.projectID || "") + (this.data.saleID || "");
  }

  get isUrgent() {
    return this.data.urgency === 3;
  }

  // Parts are needed and haven't arrived, whether or not they've been ordered
  // yet. The single definition behind the "Parts Pending" metric, list filter
  // and widget counts.
  get isPartsPending() {
    return Boolean(this.data.partsRequired && !this.data.partsReceived);
  }

  // An open issue still waiting for parts to arrive: what receiving can mark
  // received. Parts already on hand are not coming.
  get canReceiveParts() {
    return Boolean(
      !this.data.isResolved && this.isPartsPending && !this.data.partsInStock
    );
  }

  get needsQuote() {
    return this.status === "needs_quote";
  }

  get isAwaitingParts() {
    return (
      this.status === "awaiting_parts_order" || this.status === "awaiting_parts"
    );
  }

  get courtesyAuthorizedByUser() {
    return this.data.courtesyAuthorizedBy
      ? store.state.main.users[this.data.courtesyAuthorizedBy]
      : null;
  }

  get warrantyDocs() {
    return (this.data.warrantyDocuments || []).map((d) => new DocumentRef(d));
  }

  get statusType() {
    const type = SERVICE_ISSUE_STATUS[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get media() {
    return (this.data.media || []).map((d) => new DocumentRef(d));
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get tipsTricks() {
    const type = SERVICE_ISSUE_TYPES[this.data.issueType];
    return type ? type.tipsTricks || null : null;
  }

  toString() {
    const type = this.issueType;
    return type ? type.label : this.data.description.slice(0, 50);
  }
}
